import { timeout, delay } from '@ynap/promise-utils';

const apiAction = (request, prefix, uid, options = {}) => {
    return async (dispatch, getState, fetch) => {
        return multipleRequestApiAction([{ request, prefix, uid, options }], true)(dispatch, getState, fetch);
    };
};

export const multipleRequestApiAction = (asyncFetchActions, singleItem) => {
    return async (dispatch, getState, fetchAndLog = fetch) => {
        const arrayOfResponses = await Promise.all(
            asyncFetchActions.map(async (action) => {
                const { request, prefix, uid, options, required = true, coremediaPageKey, externalRequest, controller, abortFn } = action;
                try {
                    if (!request || !prefix) {
                        throw new Error('Missing request or prefix param');
                    }
                    dispatch(
                        createActionObject({
                            type: `${prefix}/REQUEST`,
                            request,
                            options,
                            uid,
                            coremediaPageKey,
                            controller,
                        }),
                    );

                    return externalRequest
                        ? await handleRequest({ request, prefix, uid, options }, fetch)
                        : await handleRequest({ request, prefix, uid, options, coremediaPageKey }, fetchAndLog);
                } catch (error) {
                    return { ...error, required, abortFn };
                }
            }),
        );
        const resolvedPromiseErrors = [];
        const returnArray = await arrayOfResponses.map((result) => {
            const { prefix, request, error, redirectUrl, required, status, headers, uid, options, json, redirected, coremediaPageKey } = result;
            if (error) {
                if (error.name === 'AbortError') {
                    result.abortFn && dispatch(result.abortFn());
                    return; // Aborting request will return an error we don't want to handle in redux
                }
                dispatch(
                    createActionObject({
                        type: `${prefix}/FAILURE`,
                        request,
                        body: error,
                        status,
                        headers,
                        uid,
                        redirectUrl,
                        options,
                        redirected,
                        coremediaPageKey,
                    }),
                );

                if (required) {
                    resolvedPromiseErrors.push(error);
                }

                return result;
            } else {
                dispatch(
                    createActionObject({
                        type: `${prefix}/SUCCESS`,
                        request,
                        body: json,
                        status,
                        headers,
                        uid,
                        redirectUrl,
                        options,
                        redirected,
                        coremediaPageKey,
                    }),
                );

                return result;
            }
        });

        if (resolvedPromiseErrors.length > 0) {
            throw singleItem ? resolvedPromiseErrors[0] : resolvedPromiseErrors;
        } else {
            return singleItem ? returnArray[0] : returnArray;
        }
    };
};

const handleRequest = async ({ request, prefix, uid, options = {}, coremediaPageKey }, fetch) => {
    const { reqTimeout = 0, reqDelay = 0, generateRedirectUrl, errorTags, bypassCreateGuestUbertoken, signal = undefined } = options;
    let headers;
    let status;
    let redirected;
    //client side auto follow request
    let redirectType = 'follow';

    //ssr manual
    if (typeof __CLIENT__ === 'undefined' || !__CLIENT__) {
        redirectType = 'manual';
    }

    try {
        const response = await delay(
            timeout(
                fetch(request, {
                    /* global __CLIENT__:true */ /* istanbul ignore next */ redirect: redirectType,
                    label: prefix,
                    errorTags,
                    bypassCreateGuestUbertoken,
                    signal,
                }),
                reqTimeout,
            ),
            reqDelay,
        );

        if (!response) throw new Error('Empty response from API');

        headers = response.headers;
        status = response.status;
        redirected = response.redirected;

        //this will only be triggered when redirect type is 'manual', i.e. ssr
        if (status >= 300 && status <= 399 && generateRedirectUrl) {
            return await handleRedirect(request, status, headers, uid, options, prefix, generateRedirectUrl);
        }

        const json = await handleResponse(response);

        return { request, status, headers, uid, options, json, prefix, redirected, coremediaPageKey };
    } catch (error) {
        throw { request, error, status, headers, uid, options, prefix, coremediaPageKey };
    }
};

const handleRedirect = async (request, status, headers, uid, options, prefix, generateRedirectUrl) => {
    const redirectUrl = generateRedirectUrl(headers.get('Location'));
    return {
        request,
        status,
        headers,
        uid,
        options,
        redirectUrl,
        prefix,
    };
};

function createActionObject({
    coremediaPageKey,
    type,
    request,
    body = null,
    status = null,
    headers = null,
    uid = null,
    redirectUrl = '',
    options = {},
    redirected = false,
    controller = undefined,
}) {
    return {
        type,
        request,
        body,
        status,
        headers,
        uid,
        redirectUrl,
        options,
        redirected,
        coremediaPageKey,
        controller,
    };
}

function handleResponse(response) {
    return new Promise((resolve, reject) => {
        if (response.ok) {
            response
                .json()
                .then((json) => resolve(json))
                .catch((parsingError) =>
                    reject({
                        message: `apiAction parsing error - ${response.status} response`,
                        error: parsingError,
                    }),
                );
        } else {
            response
                .json()
                .then((errorJson) => {
                    reject({
                        message: `apiAction error - ${response.status} response`,
                        error: errorJson,
                        // to allow server side to capture and return the  status code.
                        status: response.status,
                    });
                })
                .catch((parsingError) => {
                    reject({
                        message: `apiAction parsing error - ${response.status} response`,
                        error: parsingError,
                        // to allow server side to capture and return the  status code.
                        status: response.status,
                    });
                });
        }
    });
}

export default apiAction;
